import {
  AdminUserPreferenceResponse,
  AdminUserPreferenceScope,
  PreferenceValue,
  SINGLETON_PREFERENCE_KEY
} from '../api/types/admin-user-preference';
import { AdminUserTenant } from '../api/types/GraphQL';

/**
 * The active tenant across reloads (NBO-2368) and across machines (NBO-2517).
 *
 * - ACTIVE_TENANT_KEY (localStorage, global): in-session continuity across the hard reload a
 *   tenant switch does. Removed on logout so one user's choice never leaks to the next user on a
 *   shared machine.
 * - The `lastTenant` preference (on the account): the tenant the user last worked in, so they
 *   start there on their next login, on any machine. Written by LastTenantPreferenceService.
 */
export const ACTIVE_TENANT_KEY = 'activeTenant';

export const LAST_TENANT_PREFERENCE = 'lastTenant';

/** Writes the in-session key. The account row follows from the active tenant on its own. */
export function persistActiveTenant(tenantId: string | number): void {
  localStorage.setItem(ACTIVE_TENANT_KEY, tenantId.toString());
}

export function toLastTenantPreference(tenantId: string | number): PreferenceValue {
  return { tenantId: String(tenantId) };
}

export function lastTenantIdOf(value: unknown): string | null {
  const tenantId = (value as { tenantId?: unknown } | null | undefined)?.tenantId;
  if (typeof tenantId === 'string' && tenantId) return tenantId;
  if (typeof tenantId === 'number') return String(tenantId);
  return null;
}

/** The `lastTenant` row of the login answer's GLOBAL preferences, if it holds one. */
export function lastTenantIdFromLogin(page: AdminUserPreferenceResponse | null | undefined): string | null {
  const row = page?.items?.find(
    (item) =>
      item.type === LAST_TENANT_PREFERENCE &&
      item.key === SINGLETON_PREFERENCE_KEY &&
      item.scope === AdminUserPreferenceScope.Global
  );
  return row ? lastTenantIdOf(row.value) : null;
}

/**
 * Which tenant a login opens in: this session's tenant (a switch reloads the app), else the one
 * the user last worked in (the account), else the tenant flagged default, else the first. An id the user no longer has access to is skipped.
 */
export function resolveActiveTenant(
  tenants: readonly AdminUserTenant[],
  candidates: { session?: string | null; lastTenant?: string | null }
): AdminUserTenant | undefined {
  const byId = (raw: string | null | undefined) =>
    raw ? tenants.find((tenant) => Number(tenant.id) === Number(raw)) : undefined;
  return (
    byId(candidates.session) ||
    byId(candidates.lastTenant) ||
    tenants.find((tenant) => !!tenant.default) ||
    tenants[0]
  );
}
